/**
 * 「系统参数运维」页的全部校验 / 解析纯函数。
 *
 * 这些函数与后端 ``_validate_*`` 是**同口径**实现：前端在点保存前就把错误
 * 用中文点名报出来，后端仍做二次校验兜底。因此这里的每一条规则改动都必须
 * 与后端同步——单改前端会造成「前端放行、后端 400」或反过来的前后端漂移。
 *
 * 从原 ``index.tsx`` 单文件拆出时逻辑一字未改，仅搬移位置。
 */

export const validatePackageUrl = (url: string): string | null => {
  if (!url) return 'package_url 不能为空';
  if (!(url.startsWith('bos://') || url.startsWith('s3://'))) {
    return 'package_url 必须以 bos:// 或 s3:// 开头';
  }
  return null;
};

export const validatePackageUrlTemplate = (url: string): string | null => {
  if (!url) return '模板不能为空';
  if (!(url.startsWith('bos://') || url.startsWith('s3://') || url.startsWith('obs://'))) {
    return '模板必须以 bos:// / s3:// / obs:// 开头';
  }
  if (!url.includes('{version}')) {
    return '模板必须包含 {version} 占位符';
  }
  return null;
};

export const validateDailyBaseVersion = (value: string): string | null => {
  if (!value) return 'daily base-version 不能为空';
  return null;
};

/**
 * bazel 预设 JSON 校验：与后端 ``_validate_bazel_presets`` 同口径。
 * 失败返回中文错误（前端 message.error 直接用），成功返回 null。
 */
export const validateBazelPresetsJson = (raw: string): string | null => {
  const trimmed = (raw || '').trim();
  if (!trimmed) return 'bazel 预设 JSON 不能为空';
  let obj: unknown;
  try {
    obj = JSON.parse(trimmed);
  } catch (e) {
    return `JSON 解析失败：${(e as Error).message}`;
  }
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return 'bazel 预设必须是 JSON 对象（非数组 / 非标量）';
  }
  const dict = obj as Record<string, unknown>;
  if (Object.keys(dict).length === 0) {
    return 'bazel 预设必须是非空 JSON 对象';
  }
  for (const [key, item] of Object.entries(dict)) {
    if (!key.trim()) return '预设 key 必须是非空字符串';
    if (typeof item !== 'object' || item === null || Array.isArray(item)) {
      return `预设 ${key} 必须是对象`;
    }
    const preset = item as Record<string, unknown>;
    const label = typeof preset.label === 'string' ? preset.label.trim() : '';
    const branch = typeof preset.branchDpInfo === 'string' ? preset.branchDpInfo.trim() : '';
    const base = typeof preset.baseVersion === 'string' ? preset.baseVersion.trim() : '';
    if (!label || !branch || !base) {
      return `预设 ${key} 的 label/branchDpInfo/baseVersion 均不能为空`;
    }
  }
  return null;
};

/**
 * 主仓模块短名。它在 branchDpInfo 里出现（要解 commit），但不是编译模块，
 * 所以不进 changeModule；与后端 ``BazelCompileService._strip_main_repo_from_change_module``
 * 同一口径。
 */
export const MAIN_REPO_MODULE = 'neolix-repo';

/**
 * 解析 ``模块:分支`` 串，返回保序去重的模块名。
 * 分组分隔符与后端 ``_parse_branch_dp_modules`` 同口径（逗号 / 换行）。
 * 条目缺冒号或模块/分支任一为空时抛错，由调用方转成中文提示。
 */
export const parseBranchDpModules = (branchDpInfo: string): string[] => {
  const modules: string[] = [];
  for (const group of (branchDpInfo || '').split(/[,\n\r]+/)) {
    const entry = group.trim();
    if (!entry) continue;
    const idx = entry.indexOf(':');
    if (idx < 0) {
      throw new Error(`branchDpInfo 条目 ${JSON.stringify(entry)} 缺少 \`:\`，应为 \`模块:分支\``);
    }
    const module = entry.slice(0, idx).trim();
    const branch = entry.slice(idx + 1).trim();
    if (!module || !branch) {
      throw new Error(`branchDpInfo 条目 ${JSON.stringify(entry)} 的模块名与分支名都不能为空`);
    }
    if (!modules.includes(module)) modules.push(module);
  }
  return modules;
};

/** 逗号 / 换行分隔串 → 保序去重的非空片段 */
export const splitModuleList = (raw: string): string[] => {
  const out: string[] = [];
  for (const part of (raw || '').split(/[,\n\r]+/)) {
    const v = part.trim();
    if (v && !out.includes(v)) out.push(v);
  }
  return out;
};

/**
 * paths 子对象校验：与后端 ``_validate_module_paths_dict`` 同口径。
 *
 * 规则:
 * - 顶层为非空 JSON object;
 * - key 是非空字符串，不含空白与 ``/``（应为模块短名）;
 * - value 是非空字符串，不含空白/反斜杠，不以 ``/`` 开头或结尾，至少含一段 ``/``
 *   （GitLab module 极少直接挂仓库根，缺 group 段多半是漏配）。
 */
export const validatePathsDict = (dict: Record<string, unknown>): string | null => {
  if (Object.keys(dict).length === 0) {
    return 'paths 必须是非空 JSON 对象';
  }
  for (const [key, value] of Object.entries(dict)) {
    if (!key.trim()) return 'paths 的 key 必须是非空字符串';
    if (/\s/.test(key) || key.includes('/')) {
      return `paths key ${JSON.stringify(key)} 不能含空白或 \`/\`（应为模块短名）`;
    }
    if (typeof value !== 'string') {
      return `paths[${key}] 的值必须是字符串（GitLab full path）`;
    }
    const path = value.trim();
    if (!path) return `paths[${key}] 的值不能为空`;
    if (/\s/.test(path) || path.includes('\\')) {
      return `paths[${key}] ${JSON.stringify(path)} 不能含空白或反斜杠`;
    }
    if (path.startsWith('/') || path.endsWith('/')) {
      return `paths[${key}] ${JSON.stringify(path)} 不能以 \`/\` 开头或结尾`;
    }
    if (!path.includes('/')) {
      return `paths[${key}] ${JSON.stringify(path)} 应为 \`group/repo\` 形式；module 极少直接挂 GitLab 仓库根，请检查是否漏配 group 段`;
    }
  }
  return null;
};

/**
 * module_registry 三字段校验：与后端 ``_validate_module_registry`` 同口径。
 * 失败返回中文错误，成功返回 null。
 *
 * 除各字段自身格式外，还查跨字段不变量
 * ``changeModule ⊆ branchDpInfo 的模块 ⊆ paths 的 key``——前端就把漏配的模块名
 * 标出来，不用等后端 400，更不用等编译时 GitLab namespace not found。
 * 反向的"少"都合法：changeModule 天然少一个主仓 neolix-repo，
 * paths 可以登记当前不进 daily 的在册模块。
 */
export const validateModuleRegistry = (
  pathsJson: string,
  branchDpInfo: string,
  changeModule: string,
): string | null => {
  const trimmed = (pathsJson || '').trim();
  if (!trimmed) return 'paths JSON 不能为空';
  let obj: unknown;
  try {
    obj = JSON.parse(trimmed);
  } catch (e) {
    return `paths JSON 解析失败：${(e as Error).message}`;
  }
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return 'paths 必须是 JSON 对象（非数组 / 非标量）';
  }
  const dict = obj as Record<string, unknown>;
  const pathsErr = validatePathsDict(dict);
  if (pathsErr) return pathsErr;

  let branchModules: string[];
  try {
    branchModules = parseBranchDpModules(branchDpInfo);
  } catch (e) {
    return (e as Error).message;
  }
  if (branchModules.length === 0) return 'branchDpInfo 不能为空';

  const unknown = branchModules.filter((m) => !(m in dict));
  if (unknown.length > 0) {
    return `branchDpInfo 里的模块 ${unknown.join(', ')} 未在 paths 中登记 GitLab 路径；编译时 git ls-remote 会报 namespace not found，请先补 paths`;
  }

  const changeModules = splitModuleList(changeModule);
  if (changeModules.length === 0) return 'changeModule 不能为空';
  const orphan = changeModules.filter((m) => !branchModules.includes(m));
  if (orphan.length > 0) {
    return `changeModule 里的模块 ${orphan.join(', ')} 没有出现在 branchDpInfo 中：要编译却没指定分支，请补 branchDpInfo 或从 changeModule 移除`;
  }
  return null;
};

/**
 * evaluator_whitelist JSON 校验：与后端 ``_validate_evaluator_whitelist`` 同口径。
 * 失败返回中文错误，成功返回 null。
 *
 * 规则：
 * - 顶层为非空 JSON object（``{"<name>": "<cn_name>", ...}``）
 * - key 是非空字符串，不含空白与 ``/``（评测器英文名 snake_case）
 * - value 是非空字符串（中文展示名）
 */
export const validateEvaluatorWhitelistJson = (raw: string): string | null => {
  const trimmed = (raw || '').trim();
  if (!trimmed) return 'evaluator_whitelist JSON 不能为空';
  let obj: unknown;
  try {
    obj = JSON.parse(trimmed);
  } catch (e) {
    return `JSON 解析失败：${(e as Error).message}`;
  }
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return 'evaluator_whitelist 必须是 JSON 对象（非数组 / 非标量）';
  }
  const dict = obj as Record<string, unknown>;
  if (Object.keys(dict).length === 0) {
    return 'evaluator_whitelist 必须是非空 JSON 对象';
  }
  for (const [key, value] of Object.entries(dict)) {
    if (!key.trim()) return 'key 必须是非空字符串';
    if (/\s/.test(key) || key.includes('/')) {
      return `key ${JSON.stringify(key)} 不能含空白或 \`/\``;
    }
    const isObj = typeof value === 'object' && value !== null && !Array.isArray(value);
    const cn = isObj ? (value as Record<string, unknown>).cnName : value;
    if (typeof cn !== 'string') {
      return `[${key}] 的中文名必须是字符串`;
    }
    if (!cn.trim()) return `[${key}] 的中文名不能为空`;
    if (isObj) {
      const url = (value as Record<string, unknown>).feishuUrl ?? '';
      if (typeof url !== 'string') return `[${key}] 的飞书文档链接必须是字符串`;
      if (url.trim() && !/^https?:\/\//i.test(url.trim())) {
        return `[${key}] 的飞书文档链接必须以 http:// 或 https:// 开头`;
      }
    }
  }
  return null;
};

const BUCKET_NAME_RE = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/;

/**
 * 产物桶路由表校验：与后端 ``_validate_output_bucket_routing`` 同口径（结构、桶名、去重、
 * writable ⊆ buckets ∪ {原桶}、buckets 只增不减）。``currentRaw`` 为库里当前值，
 * 传入时本地就拦住删 buckets，并直接告诉用户正确的停写/回滚方式；后端仍会二次校验。
 */
export const validateOutputBucketRoutingJson = (
  raw: string, defaultBucket: string, currentRaw?: string,
): string | null => {
  const trimmed = (raw || '').trim();
  if (!trimmed) return '产物桶路由表 JSON 不能为空';
  let obj: unknown;
  try {
    obj = JSON.parse(trimmed);
  } catch (e) {
    return `JSON 解析失败：${(e as Error).message}`;
  }
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return '路由表必须是 JSON 对象';
  }
  const lists: Record<string, string[]> = {};
  for (const field of ['buckets', 'writable']) {
    const val = (obj as Record<string, unknown>)[field] ?? [];
    if (!Array.isArray(val) || !val.every((x) => typeof x === 'string')) {
      return `路由表 ${field} 必须是字符串数组`;
    }
    const names = (val as string[]).map((x) => x.trim());
    const bad = names.find((x) => !BUCKET_NAME_RE.test(x));
    if (bad !== undefined) return `非法 bucket 名：${JSON.stringify(bad)}`;
    if (new Set(names).size !== names.length) return `${field} 不能有重复项`;
    lists[field] = names;
  }
  const allowed = new Set([...lists.buckets, defaultBucket]);
  const missing = lists.writable.filter((b) => !allowed.has(b));
  if (missing.length) return `writable 中的桶必须先登记到 buckets：${missing.join(', ')}`;
  const current = parseBucketList(currentRaw, 'buckets');
  const dropped = current.filter((b) => !lists.buckets.includes(b));
  if (dropped.length) {
    return `buckets 不能删除（${dropped.join(', ')} 里仍有 task 产物）。`
      + '停止写入：只清空或删减 writable；整体回滚：点右侧「回滚」按钮';
  }
  return null;
};

function parseBucketList(raw: string | undefined, field: string): string[] {
  if (!raw) return [];
  try {
    const val = (JSON.parse(raw) as Record<string, unknown>)[field];
    return Array.isArray(val) ? val.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Case-Flow 查询全局并发上限校验：与后端
 * ``_validate_caseflow_query_concurrency`` 同口径。
 *
 * 必须是 1–200 的整数（上限与 query_pool 的进程内收敛口共用同一个常量）。
 * 失败返回中文错误，成功返回 null。
 */
export const validateCaseflowQueryConcurrency = (raw: string): string | null => {
  const trimmed = (raw || '').trim();
  if (!trimmed) return 'Case-Flow 查询并发上限不能为空';
  if (!/^\d+$/.test(trimmed)) return 'Case-Flow 查询并发上限必须是整数';
  const n = parseInt(trimmed, 10);
  if (n < 1 || n > 200) return 'Case-Flow 查询并发上限必须在 1–200 之间';
  return null;
};

/**
 * 预测数据开放开关校验：与后端 ``_validate_prediction_open_enabled`` 同口径。
 *
 * 只认 ``true`` / ``false``（大小写不敏感）。失败返回中文错误，成功返回 null。
 * 注意这是**开关**值本身的校验；前端渲染成 Switch，正常路径下用户点不出非法值，
 * 保留它是为了与后端「同口径」这条约定一致（从别处粘贴/程序化写入仍会经过这里）。
 */
export const validatePredictionOpenEnabled = (raw: string): string | null => {
  const trimmed = (raw || '').trim().toLowerCase();
  if (trimmed !== 'true' && trimmed !== 'false') {
    return '预测数据开放开关只能是 true 或 false';
  }
  return null;
};

/**
 * 「创建任务默认预测数据版本」校验：与后端
 * ``_validate_prediction_job_default_version`` 同口径。
 *
 * 只认 ``default`` / ``latest``（大小写不敏感），保存时会归一化成小写。
 */
export const validatePredictionJobDefaultVersion = (raw: string): string | null => {
  const trimmed = (raw || '').trim().toLowerCase();
  if (trimmed !== 'default' && trimmed !== 'latest') {
    return '默认预测数据版本只能是 default 或 latest';
  }
  return null;
};

/**
 * 参数型评估器·指标聚合声明 JSON 校验（与后端 ``_validate_evaluator_metric_agg`` 同口径）。
 *
 * 契约：``{"<evaluator>": {label?, trigger_key?, sum_keys?, rates?, distribution_keys?, charts?}}``。
 * - 顶层必须是非空 JSON 对象；每个评估器 key 非空字符串、spec 为对象；
 * - ``sum_keys`` 每项是非空字符串或 ``{key, label?}`` 对象；
 * - ``distribution_keys`` 若存在必须是非空字符串数组；
 * - ``rates`` 每条含非空字符串 ``key`` / ``numerator`` / ``denominator`` + 数值 ``scale``
 *   （率 = 先 ∑numerator、∑denominator 再相除 ×scale，三者缺一没法算）；
 * - ``charts`` 若存在只是展示层声明（不改聚合口径），与后端 ``_validate_metric_agg_charts``
 *   同口径：id 在评估器内唯一、type ∈ {donut,bar}、series 引用的 key 必须在 sum_keys/rates、
 *   {prefix,exclude} 至少匹配 1 个 sum_keys、percent_base 只能是 ``"sum"`` 或
 *   ``"key:<sum_keys 键>"`` 且仅 donut 可写。
 */
export const validateEvaluatorMetricAggJson = (raw: string): string | null => {
  const trimmed = (raw || '').trim();
  if (!trimmed) return 'evaluator_metric_agg JSON 不能为空';
  let obj: unknown;
  try {
    obj = JSON.parse(trimmed);
  } catch (e) {
    return `JSON 解析失败：${(e as Error).message}`;
  }
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return 'evaluator_metric_agg 必须是 JSON 对象（非数组 / 非标量）';
  }
  const dict = obj as Record<string, unknown>;
  if (Object.keys(dict).length === 0) {
    return 'evaluator_metric_agg 必须是非空 JSON 对象';
  }
  const isNonEmptyStr = (x: unknown): x is string => typeof x === 'string' && x.trim() !== '';
  for (const [name, spec] of Object.entries(dict)) {
    if (!name.trim()) return 'evaluator_metric_agg 的 key 必须是非空字符串';
    if (typeof spec !== 'object' || spec === null || Array.isArray(spec)) {
      return `[${name}] 的 spec 必须是对象`;
    }
    const s = spec as Record<string, unknown>;
    const sumKeys = s.sum_keys;
    if (sumKeys !== undefined) {
      if (!Array.isArray(sumKeys)) return `[${name}].sum_keys 必须是数组`;
      for (let i = 0; i < sumKeys.length; i += 1) {
        const x = sumKeys[i];
        if (typeof x === 'string') {
          if (!x.trim()) return `[${name}].sum_keys[${i}] 字符串不能为空`;
        } else if (typeof x === 'object' && x !== null && !Array.isArray(x)) {
          const kv = x as Record<string, unknown>;
          if (!isNonEmptyStr(kv.key)) return `[${name}].sum_keys[${i}].key 必须是非空字符串`;
          if (kv.label !== undefined && !isNonEmptyStr(kv.label)) {
            return `[${name}].sum_keys[${i}].label 必须是非空字符串`;
          }
        } else {
          return `[${name}].sum_keys[${i}] 必须是字符串或 {key,label} 对象`;
        }
      }
    }
    const distKeys = s.distribution_keys;
    if (distKeys !== undefined) {
      if (!Array.isArray(distKeys) || distKeys.some((x) => !isNonEmptyStr(x))) {
        return `[${name}].distribution_keys 必须是非空字符串数组`;
      }
    }
    const rates = s.rates;
    if (rates !== undefined) {
      if (!Array.isArray(rates)) return `[${name}].rates 必须是数组`;
      for (let i = 0; i < rates.length; i += 1) {
        const rate = rates[i];
        if (typeof rate !== 'object' || rate === null || Array.isArray(rate)) {
          return `[${name}].rates[${i}] 必须是对象`;
        }
        const r = rate as Record<string, unknown>;
        for (const f of ['key', 'numerator', 'denominator'] as const) {
          if (!isNonEmptyStr(r[f])) return `[${name}].rates[${i}].${f} 必须是非空字符串`;
        }
        if (typeof r.scale !== 'number' || Number.isNaN(r.scale)) {
          return `[${name}].rates[${i}].scale 必须是数值`;
        }
      }
    }
    // —— charts：展示层声明，引用的 key 必须真实存在于 sum_keys / rates ——
    const charts = s.charts;
    if (charts !== undefined) {
      if (!Array.isArray(charts)) return `[${name}].charts 必须是数组`;
      const sumKeyNames = (Array.isArray(s.sum_keys) ? s.sum_keys : [])
        .map((x) => (typeof x === 'string' ? x : (x as Record<string, unknown>)?.key))
        .filter((k): k is string => typeof k === 'string' && k.trim() !== '');
      const rateKeyNames = (Array.isArray(rates) ? rates : [])
        .map((x) => (x as Record<string, unknown>)?.key)
        .filter((k): k is string => typeof k === 'string' && k.trim() !== '');
      const seenIds = new Set<string>();
      for (let i = 0; i < charts.length; i += 1) {
        const ctx = `[${name}].charts[${i}]`;
        const chart = charts[i];
        if (typeof chart !== 'object' || chart === null || Array.isArray(chart)) {
          return `${ctx} 必须是对象`;
        }
        const c = chart as Record<string, unknown>;
        if (!isNonEmptyStr(c.id)) return `${ctx}.id 必须是非空字符串`;
        if (seenIds.has(c.id)) return `${ctx}.id 重复：${c.id}`;
        seenIds.add(c.id);
        if (c.title !== undefined && !isNonEmptyStr(c.title)) {
          return `${ctx}.title 必须是非空字符串`;
        }
        if (c.type !== 'donut' && c.type !== 'bar') {
          return `${ctx}.type 必须是 donut/bar 之一`;
        }
        const series = c.series;
        if (Array.isArray(series)) {
          const bad = series.filter(
            (k) => !isNonEmptyStr(k) || (!sumKeyNames.includes(k) && !rateKeyNames.includes(k)),
          );
          if (series.length === 0 || bad.length > 0) {
            return `${ctx}.series 为空或引用了不在 sum_keys/rates 里的 key：${bad.join(', ')}`;
          }
        } else if (typeof series === 'object' && series !== null && !Array.isArray(series)) {
          const sp = series as Record<string, unknown>;
          const prefix = sp.prefix;
          if (!isNonEmptyStr(prefix)) return `${ctx}.series.prefix 必须是非空字符串`;
          const exclude = sp.exclude;
          if (exclude !== undefined && (!Array.isArray(exclude) || exclude.some((x) => typeof x !== 'string'))) {
            return `${ctx}.series.exclude 必须是字符串数组`;
          }
          const ex = Array.isArray(exclude) ? (exclude as string[]) : [];
          const matched = sumKeyNames.filter((k) => k.startsWith(prefix) && !ex.includes(k));
          if (matched.length === 0) {
            return `${ctx}.series.prefix 没有匹配到任何 sum_keys：${prefix}`;
          }
        } else {
          return `${ctx}.series 必须是 key 数组或 {prefix, exclude?} 对象`;
        }
        const base = c.percent_base;
        if (base !== undefined) {
          if (c.type !== 'donut') return `${ctx}.percent_base 只对 donut 有意义`;
          if (base !== 'sum' && !(typeof base === 'string' && base.startsWith('key:') && sumKeyNames.includes(base.slice(4)))) {
            return `${ctx}.percent_base 必须是 "sum" 或 "key:<sum_keys 里的 key>"`;
          }
        }
      }
    }
  }
  return null;
};
